import { useState } from "react";

export interface FilterGroup {
  key: string;
  label: string;
  options: string[];
}

interface FilterBarProps {
  groups: FilterGroup[];
  selected: Record<string, string[]>;
  onToggle: (groupKey: string, value: string) => void;
  onReset: () => void;
  resultCount: number;
}

function FilterPanel({ groups, selected, onToggle }: Omit<FilterBarProps, "onReset" | "resultCount">) {
  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <div key={group.key} className="flex flex-col gap-2 md:flex-row md:items-start">
          <span className="w-20 shrink-0 pt-1 text-sm font-medium text-foreground-500">
            {group.label}
          </span>
          <div className="flex flex-wrap gap-2">
            {group.options.map((option) => {
              const active = selected[group.key]?.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => onToggle(group.key, option)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200 text-foreground-700 hover:bg-background-300"
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FilterBar({ groups, selected, onToggle, onReset, resultCount }: FilterBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeCount = Object.values(selected).reduce((sum, list) => sum + list.length, 0);

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
      <div className="hidden md:block">
        <FilterPanel groups={groups} selected={selected} onToggle={onToggle} />
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
          <span className="text-sm text-foreground-600">
            共筛选出 <strong className="text-primary-700">{resultCount}</strong> 条内容
          </span>
          <button
            type="button"
            onClick={onReset}
            disabled={activeCount === 0}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between md:hidden">
        <span className="text-sm text-foreground-600">
          共 <strong className="text-primary-700">{resultCount}</strong> 条内容
        </span>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50"
        >
          <i className="ri-filter-3-line"></i>
          筛选
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-background-50 px-1 text-[11px] font-bold text-primary-700">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-black text-foreground-950">筛选条件</h3>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel groups={groups} selected={selected} onToggle={onToggle} />
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700"
              >
                重置筛选
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 whitespace-nowrap rounded-md bg-primary-500 py-2.5 text-sm font-bold text-background-50"
              >
                查看 {resultCount} 条结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}